import { AddressedTaskListItem, OrganizerEmailDto, Task, TaskDto, TaskListItem, TaskStatusType } from 'types/tasks';
import { APICorrespondenceSlice } from './correspondence';
import { APIDebriefingsSlice } from './debriefings';
import { APINotificationsSlice } from './notifications';
import { portalAPI } from './portalAPI';
import { accumulatedQueryWithFilters } from './util/endpointBuilder';

export const APITasksSlice = portalAPI
    .enhanceEndpoints({ addTagTypes: ['Tasks', 'AddressedTaskListItems', 'TaskListItems'] })
    .injectEndpoints({
        endpoints: (builder) => ({
            getTaskById: builder.query<Task, string>({
                query: (entityId) => `v1/tasks/${entityId}`,
                keepUnusedDataFor: 0,
                providesTags: (_1, _2, arg) => [{ type: 'Tasks', id: arg }],
            }),
            getTasksWithFilters: accumulatedQueryWithFilters<TaskListItem>(builder, '/v1/tasks', {
                providesTags: ['TaskListItems'],
            }),
            getTasksAddressedToEmployee: builder.query<AddressedTaskListItem[], { employeeId: number; search: string }>(
                {
                    query: ({ employeeId, search }) => ({
                        url: '/v1/tasks/assigned',
                        params: { employeeId, searchText: search },
                    }),
                    keepUnusedDataFor: 0,
                    providesTags: (_1, _2, arg) => [{ type: 'AddressedTaskListItems', task: arg }],
                }
            ),
            addTask: builder.mutation<Task, TaskDto>({
                query: ({ ...body }) => ({
                    url: '/v1/tasks',
                    method: 'POST',
                    body,
                }),
                invalidatesTags: ['Tasks', 'AddressedTaskListItems', 'TaskListItems'],
                async onQueryStarted(_, { dispatch, queryFulfilled }) {
                    await queryFulfilled;
                    dispatch(APICorrespondenceSlice.util.invalidateTags(['CorrespondenceList']));
                    dispatch(
                        APIDebriefingsSlice.util.invalidateTags(['DebriefingListItems', 'DebriefingClientRollup'])
                    );
                },
            }),
            updateTask: builder.mutation<Task, TaskDto>({
                query: ({ id, ...body }) => ({
                    url: `/v1/tasks/${id}`,
                    method: 'PUT',
                    body,
                }),
                invalidatesTags: (_1, _2, arg) =>
                    arg.id
                        ? ['Tasks', 'AddressedTaskListItems', 'TaskListItems', { type: 'Tasks', id: arg.id }]
                        : ['Tasks', 'AddressedTaskListItems', 'TaskListItems'],
                async onQueryStarted(_, { dispatch, queryFulfilled }) {
                    await queryFulfilled;
                    dispatch(APICorrespondenceSlice.util.invalidateTags(['CorrespondenceList']));
                    dispatch(
                        APIDebriefingsSlice.util.invalidateTags(['DebriefingListItems', 'DebriefingClientRollup'])
                    );
                    dispatch(APINotificationsSlice.util.invalidateTags(['Notifications']));
                },
            }),
            deleteTask: builder.mutation<boolean, { id: string }>({
                query: ({ id }) => ({
                    url: `/v1/tasks/${id}`,
                    method: 'DELETE',
                }),
                invalidatesTags: ['Tasks', 'AddressedTaskListItems', 'TaskListItems'],
                async onQueryStarted(_, { dispatch, queryFulfilled }) {
                    await queryFulfilled;
                    dispatch(APICorrespondenceSlice.util.invalidateTags(['CorrespondenceList']));
                    dispatch(
                        APIDebriefingsSlice.util.invalidateTags(['DebriefingListItems', 'DebriefingClientRollup'])
                    );
                },
            }),
            switchTasksStatus: builder.mutation<
                boolean,
                // `nextStatus`, the status the switch leads to, shows it on the task and its list rows before the answer.
                { id: string; nextStatus?: `${TaskStatusType}` }
            >({
                query: ({ id }) => ({
                    url: `/v1/tasks/${id}/switch-status`,
                    method: 'PUT',
                }),
                invalidatesTags: (_1, _2, arg) =>
                    arg.id
                        ? ['Tasks', 'AddressedTaskListItems', 'TaskListItems', { type: 'Tasks', id: arg.id }]
                        : ['Tasks', 'AddressedTaskListItems', 'TaskListItems'],
                onQueryStarted: async ({ id, nextStatus }, { dispatch, getState, queryFulfilled }) => {
                    // The task's page and every cached tasks list take the status at once; a failed switch takes it
                    // back, and the API layer toasts it.
                    const patches = nextStatus
                        ? [
                              dispatch(
                                  APITasksSlice.util.updateQueryData('getTaskById', id, (task) => {
                                      task.taskStatusTypeCode = nextStatus;
                                  })
                              ),
                              ...APITasksSlice.util
                                  .selectInvalidatedBy(getState(), ['TaskListItems'])
                                  .filter(({ endpointName }) => endpointName === 'getTasksWithFilters')
                                  .map(({ originalArgs }) =>
                                      dispatch(
                                          APITasksSlice.util.updateQueryData(
                                              'getTasksWithFilters',
                                              originalArgs,
                                              (list) => {
                                                  const row = list.content.find((item) => String(item.id) === id);
                                                  if (row) row.taskStatusTypeCode = nextStatus;
                                              }
                                          )
                                      )
                                  ),
                          ]
                        : [];
                    try {
                        await queryFulfilled;
                    } catch {
                        patches.forEach((patch) => patch.undo());
                        return;
                    }
                    dispatch(APINotificationsSlice.util.invalidateTags(['Notifications']));
                    // Done/total counts on debriefing rows and the client rollup shift with status.
                    dispatch(
                        APIDebriefingsSlice.util.invalidateTags(['DebriefingListItems', 'DebriefingClientRollup'])
                    );
                },
            }),
            cancelTaskCalendarEvent: builder.mutation<Task, { id: string }>({
                query: ({ id }) => ({
                    url: `/v1/tasks/${id}/calendar-event`,
                    method: 'DELETE',
                }),
                invalidatesTags: (_1, _2, arg) =>
                    arg.id
                        ? ['Tasks', 'AddressedTaskListItems', 'TaskListItems', { type: 'Tasks', id: arg.id }]
                        : ['Tasks', 'AddressedTaskListItems', 'TaskListItems'],
            }),
            getOrganizerEmail: builder.query<
                OrganizerEmailDto,
                { taskTypeCodes?: string[]; isOfficial?: boolean; hasDeadline?: boolean }
            >({
                query: ({ taskTypeCodes, isOfficial = false, hasDeadline = false }) => ({
                    url: '/v1/tasks/organizer-email',
                    params: {
                        ...(taskTypeCodes ? { taskTypeCodes } : {}),
                        isOfficial,
                        hasDeadline,
                    },
                }),
            }),
        }),
    });

export const {
    useGetTaskByIdQuery,
    useGetTasksAddressedToEmployeeQuery,
    useLazyGetTasksAddressedToEmployeeQuery,
    useLazyGetTasksWithFiltersQuery,
    useAddTaskMutation,
    useUpdateTaskMutation,
    useDeleteTaskMutation,
    useSwitchTasksStatusMutation,
    useCancelTaskCalendarEventMutation,
    useLazyGetOrganizerEmailQuery,
} = APITasksSlice;
